/* ============================================================
   profileView — CB-1 (0138037b): pure view models for the
   customer profile drawer. Mirrors the backend ProfileService
   contract (DisplayName 1–100 after trim; phone = optional
   leading +, digits/spaces/dashes/parentheses, 10–15 digits,
   empty string clears; new password ≥ 8 chars).
   Pure functions only — no React, no fetch. Node-testable.
   ============================================================ */

import type { Profile, UpdateProfileRequest, ChangePasswordRequest } from '../types/auth';

export const DISPLAY_NAME_MAX = 100;
export const PASSWORD_MIN = 8;

const PHONE_SHAPE = /^[+]?[0-9 ()-]+$/;

export interface ProfileDraft {
  displayName: string;
  /** Editable text field; '' means «clear the number». */
  phoneNumber: string;
}

/** Editable draft seeded from the loaded profile. */
export function buildProfileDraft(profile: Profile): ProfileDraft {
  return {
    displayName: profile.displayName,
    phoneNumber: profile.phoneNumber ?? '',
  };
}

/** ru error for the display name, or null when valid. */
export function displayNameError(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length < 1 || trimmed.length > DISPLAY_NAME_MAX) {
    return `Имя — от 1 до ${DISPLAY_NAME_MAX} символов.`;
  }
  return null;
}

export type PhoneParse =
  | { ok: true; phone: string } // trimmed value ('' = clear)
  | { ok: false; error: string };

/** Normalize the phone input the same way the server does. */
export function parsePhoneInput(raw: string): PhoneParse {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: true, phone: '' };
  if (!PHONE_SHAPE.test(trimmed)) {
    return { ok: false, error: 'Телефон — только цифры, пробелы, дефисы и скобки.' };
  }
  const digits = trimmed.replace(/\D/g, '').length;
  if (digits < 10 || digits > 15) {
    return { ok: false, error: 'Телефон должен содержать 10–15 цифр.' };
  }
  return { ok: true, phone: trimmed };
}

/** ru error for the new password, or null when valid. */
export function newPasswordError(pw: string): string | null {
  if (pw.length < PASSWORD_MIN) {
    return `Новый пароль — минимум ${PASSWORD_MIN} символов.`;
  }
  return null;
}

/** True when the draft differs from the loaded profile (trimmed compare). */
export function isProfileDirty(draft: ProfileDraft, profile: Profile): boolean {
  const nameChanged = draft.displayName.trim() !== profile.displayName;
  const phoneNow = draft.phoneNumber.trim();
  const phoneBefore = profile.phoneNumber ?? '';
  return nameChanged || phoneNow !== phoneBefore;
}

/** Profile drawer save button is enabled only with changes and a valid name. */
export function canSaveProfile(draft: ProfileDraft, profile: Profile): boolean {
  return isProfileDirty(draft, profile) && displayNameError(draft.displayName) === null;
}

/**
 * Partial update payload: only changed fields are sent (backend keeps
 * values for null fields; '' explicitly clears the phone).
 */
export function buildUpdateRequest(draft: ProfileDraft, profile: Profile): UpdateProfileRequest {
  const request: UpdateProfileRequest = {};
  if (draft.displayName.trim() !== profile.displayName) {
    request.displayName = draft.displayName.trim();
  }
  if (draft.phoneNumber.trim() !== (profile.phoneNumber ?? '')) {
    request.phoneNumber = draft.phoneNumber.trim();
  }
  return request;
}

export type PasswordSubmit =
  | { ok: true; request: ChangePasswordRequest }
  | { ok: false; error: string };

/** Validate the change-password form before hitting the API. */
export function buildPasswordSubmit(
  currentPassword: string,
  newPassword: string,
  repeatPassword: string,
): PasswordSubmit {
  if (currentPassword.length === 0) {
    return { ok: false, error: 'Введите текущий пароль.' };
  }
  const pwError = newPasswordError(newPassword);
  if (pwError !== null) {
    return { ok: false, error: pwError };
  }
  if (newPassword !== repeatPassword) {
    return { ok: false, error: 'Новые пароли не совпадают.' };
  }
  return { ok: true, request: { currentPassword, newPassword } };
}

/** Render-ready role/balance lines for the read-only section of the drawer. */
export function profileHeaderView(profile: Profile, roleLabel: string): {
  roleLine: string;
  balanceLine: string;
} {
  const balance = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(profile.balance);
  return {
    roleLine: roleLabel,
    balanceLine: `Баланс: ${balance}`,
  };
}
